import { Component, computed, input, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { updatedAtSignal } from '../analysis-data';
import { v3ComponentContext } from '../services/v3-component-context';
import { analystsAnalysisData, RatingCategory } from '../services/analysts-analysis-data';
import { TimeAgo } from '../time-ago/time-ago';
import { PriceLineChart } from '../price-target-chart/price-line-chart';
import { CountUp } from '../count-up/count-up';
import { DataSources } from '../data-sources';
import { V3AnalystsDesktopContent } from '../v3-analysts-desktop-content/v3-analysts-desktop-content';
import { V3AnalystsRatingItem } from '../v3-analysts-rating-item/v3-analysts-rating-item';
import { V3AnalystsStatsRow } from '../v3-analysts-stats-row/v3-analysts-stats-row';
import { AnalystRating } from '../analysts-data';
import { V3TabPanel } from '../v3-tab-panel/v3-tab-panel';

// V3's Analysten detail sub-page — same content as V3AnalystsOverview's desktop card, without
// its chrome (V3Root supplies that). Desktop reuses V3AnalystsDesktopContent's [isDetail]
// variant. Mobile has Rating Konsens + Kursziele + Argumente & Thesen (category tabs) + Thesen.
// See CLAUDE.md.
@Component({
  selector: 'app-v3-analysts-analysis',
  imports: [
    NgTemplateOutlet,
    V3AnalystsDesktopContent,
    TimeAgo,
    PriceLineChart,
    V3AnalystsRatingItem,
    CountUp,
    V3AnalystsStatsRow,
    V3TabPanel,
  ],
  standalone: true,
  templateUrl: './v3-analysts-analysis.html',
  styleUrl: './v3-analysts-analysis.scss',
})
export class V3AnalystsAnalysis {
  private readonly ctx = v3ComponentContext();

  readonly currentPrice = input<number | null>(null);
  readonly dataSources = DataSources;

  readonly isZeroDesktop = this.ctx.layout.isZeroDesktop;
  readonly isFinnetDesktop = this.ctx.layout.isFinnetDesktop;
  readonly isFinnet = this.ctx.layout.isFinnet;

  private readonly data = analystsAnalysisData(this.currentPrice, this.isFinnet);

  readonly updatedAt = updatedAtSignal(this.data.analystsAnalysis);

  readonly hasContent = this.data.hasContent;
  readonly rating = this.data.rating;
  readonly isPositive = this.data.isPositive;
  readonly buyCount = this.data.buyCount;
  readonly holdCount = this.data.holdCount;
  readonly sellCount = this.data.sellCount;
  readonly gaugeValue = this.data.gaugeValue;
  readonly sellGaugeValue = this.data.sellGaugeValue;
  readonly avgTarget = this.data.avgTarget;
  readonly highTarget = this.data.highTarget;
  readonly lowTarget = this.data.lowTarget;
  readonly targetCurrency = this.data.targetCurrency;
  readonly avgTargetEuro = this.data.avgTargetEuro;
  readonly potential = this.data.potential;
  readonly targetIsPositive = this.data.targetIsPositive;
  readonly analystCount = this.data.analystCount;
  readonly consensusHistory = this.data.consensusHistory;
  readonly isZeroChartData = this.data.isZeroChartData;
  readonly chartPrimaryColor = this.data.chartPrimaryColor;
  readonly chartTargetLabelColor = this.data.chartTargetLabelColor;
  readonly chartTargetPositiveColor = this.data.chartTargetPositiveColor;
  readonly chartTargetNegativeColor = this.data.chartTargetNegativeColor;
  readonly ratings = this.data.ratings;
  readonly hasAnyRatings = this.data.hasAnyRatings;
  readonly availableRatingTabs = this.data.availableRatingTabs;
  readonly activeRatingTab = this.data.activeRatingTab;
  readonly ratingTabLabel = this.data.ratingTabLabel;
  readonly ratingTabCount = this.data.ratingTabCount;
  readonly ratingKey = this.data.ratingKey;
  readonly showPositiveTheses = this.data.showPositiveTheses;

  // No "· TIPRANKS LTD." suffix (unlike the desktop isDetail variant) — the card already has
  // its own "Quelle: TIPRANKS LTD." line below.
  readonly analystCountLegend = computed(() => {
    const count = this.analystCount();
    return count === null ? '' : `${count} Häuser`;
  });

  // Mobile only ever shows the active tab's full category — no "just the latest one" overview
  // variant here, unlike V3AnalystsDesktopContent's visibleRatings.
  readonly visibleRatings = computed<AnalystRating[]>(() => {
    const active = this.activeRatingTab();
    return active ? this.data.ratingsByCategory()[active] : [];
  });

  // Only the first 4 ratings show; the 4th fades (no toggle of its own) and an expand control
  // reveals the rest — one-way, no collapse back. Resets when the tab changes.
  readonly ratingsExpanded = signal(false);
  readonly hasMoreRatings = computed(() => this.visibleRatings().length > 4);

  readonly fullRatings = computed(() => {
    const all = this.visibleRatings();
    return this.ratingsExpanded() || !this.hasMoreRatings() ? all : all.slice(0, 4);
  });

  // Whether the collapsed 4th row should get the fade treatment right now.
  readonly showRatingsFade = computed(() => this.hasMoreRatings() && !this.ratingsExpanded());

  expandRatings() {
    this.ratingsExpanded.set(true);
  }

  selectRatingTab(category: RatingCategory) {
    this.data.selectRatingTab(category);
    this.ratingsExpanded.set(false);
  }

  // Capped at 3 for now. Always-expanded, no toggle — same detail-page convention as
  // V3AnalystsDesktopContent's own thesis list. The Hold tab shows none.
  readonly theses = computed(() =>
    this.activeRatingTab() === 'hold' ? [] : this.data.theses().slice(0, 3),
  );
}
